import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatCurrency } from '../utils/currency';
import { getApiUrl } from '../config';
import './Dashboard.css';

const Dashboard = ({ config }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [balances, setBalances] = useState([]);
  const [upcomingTransactions, setUpcomingTransactions] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [error, setError] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState({
    assets: true,
    liabilities: true,
    income: false,
    expenses: false,
    equity: false
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [transactionToAdd, setTransactionToAdd] = useState({
    date: '',
    payee: '',
    note: '',
    postings: []
  });
  const [accounts, setAccounts] = useState([]);
  const [budgetPeriod, setBudgetPeriod] = useState(() => {
    // Default to current month in YYYY-MM format
    const now = new Date();
    return now.toISOString().substring(0, 7);
  });
  const [expandedRecentTxs, setExpandedRecentTxs] = useState(new Set());

  useEffect(() => {
    loadDashboardData();
    loadAccounts();
  }, [config, budgetPeriod]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError('');

    try {
      // Load balances - use tree mode for grouping with subtotals
      const balanceResponse = await axios.post(getApiUrl('/api/hledger/balance'), {
        journalPath: config.journalPath,
        args: '--tree --no-elide'
      });

      // The actual accounts are in the first element of the data array
      const balanceData = balanceResponse.data.data || [];
      setBalances(balanceData[0] || []);

      // Load recent transactions - get last 2 weeks (14 days)
      const twoWeeksAgo = new Date();
      twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
      const startDate = twoWeeksAgo.toISOString().split('T')[0]; // YYYY-MM-DD format

      const printResponse = await axios.post(getApiUrl('/api/hledger/command'), {
        journalPath: config.journalPath,
        command: `print -b ${startDate} --output-format=json`
      });

      // Print returns full transaction objects from the last 2 weeks, reverse to show newest first
      const printData = JSON.parse(printResponse.data.stdout || '[]');
      setRecentTransactions(printData.reverse());

      // Load upcoming transactions if configured
      if (config.transactionsPath) {
        try {
          const upcomingResponse = await axios.post(getApiUrl('/api/transactions/read'), {
            filePath: config.transactionsPath
          });

          // Filter and sort upcoming transactions
          const today = new Date();
          const upcoming = upcomingResponse.data.transactions
            .map(t => ({
              ...t,
              dueDate: new Date(t.due_date)
            }))
            .filter(t => {
              const daysUntil = Math.ceil((t.dueDate - today) / (1000 * 60 * 60 * 24));
              return daysUntil <= 14; // Show transactions due within 14 days
            })
            .sort((a, b) => a.dueDate - b.dueDate);

          setUpcomingTransactions(upcoming);
        } catch (err) {
          console.log('No upcoming transactions file found');
        }
      }

      // Load budget data
      try {
        // Convert YYYY-MM to hledger period format (YYYY/MM)
        const hledgerPeriod = budgetPeriod.replace('-', '/');
        const budgetResponse = await axios.post(getApiUrl('/api/hledger/budget'), {
          journalPath: config.journalPath,
          period: hledgerPeriod
        });

        // Budget data uses periodic report format: {prDates, prRows, prTotals}
        const budgetData = budgetResponse.data.data;
        if (budgetData && budgetData.prRows && Array.isArray(budgetData.prRows)) {
          // Parse budget items from prRows
          const budgetItems = budgetData.prRows
            .filter(row => {
              // Skip <unbudgeted> and parent accounts (Assets, Expenses)
              const accountName = row.prrName;
              return accountName !== '<unbudgeted>' && accountName.includes(':');
            })
            .map(row => {
              const accountName = row.prrName;
              let actual = 0;
              let budgeted = 0;

              // prrTotal[0] is array of actual amounts
              // prrTotal[1] is array of budget amounts
              if (row.prrTotal && Array.isArray(row.prrTotal)) {
                if (row.prrTotal[0] && Array.isArray(row.prrTotal[0]) && row.prrTotal[0].length > 0) {
                  actual = Math.abs(row.prrTotal[0][0]?.aquantity?.floatingPoint || 0);
                }
                if (row.prrTotal[1] && Array.isArray(row.prrTotal[1]) && row.prrTotal[1].length > 0) {
                  budgeted = Math.abs(row.prrTotal[1][0]?.aquantity?.floatingPoint || 0);
                }
              }

              return {
                accountName,
                actual,
                budgeted
              };
            })
            .filter(item => item.budgeted > 0); // Only include accounts with budgets

          setBudgets(budgetItems);
        } else {
          setBudgets([]);
        }
      } catch (err) {
        console.log('No budget data found:', err.message);
        setBudgets([]);
      }

      setLoading(false);
    } catch (err) {
      setError('Error loading dashboard data: ' + (err.response?.data?.error || err.message));
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const response = await axios.post(getApiUrl('/api/hledger/accounts'), {
        journalPath: config.journalPath
      });
      setAccounts(response.data.accounts);
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  };

  const navigateBudgetMonth = (direction) => {
    const currentDate = new Date(budgetPeriod + '-01');
    currentDate.setMonth(currentDate.getMonth() + direction);
    const newMonth = currentDate.toISOString().substring(0, 7);
    setBudgetPeriod(newMonth);
  };

  const goToCurrentMonth = () => {
    const now = new Date();
    setBudgetPeriod(now.toISOString().substring(0, 7));
  };

  const saveTransactions = async (txs) => {
    try {
      await axios.post(getApiUrl('/api/transactions/write'), {
        filePath: config.transactionsPath,
        transactions: txs
      });
    } catch (err) {
      throw new Error('Failed to save transactions: ' + err.message);
    }
  };

  const calculateNextDueDate = (currentDate, frequency) => {
    const date = new Date(currentDate);
    const originalDay = date.getDate();
    const originalMonth = date.getMonth();

    switch (frequency) {
      case 'yearly':
        date.setFullYear(date.getFullYear() + 1);
        // Handle leap year edge case (Feb 29 -> Mar 1 in non-leap years)
        if (date.getMonth() !== originalMonth) {
          date.setDate(0); // Set to Feb 28 instead of Mar 1
        }
        break;
      case 'monthly':
        date.setMonth(date.getMonth() + 1);
        // Handle month-end dates (e.g., Jan 31 -> Feb 28/29)
        if (date.getDate() !== originalDay) {
          date.setDate(0); // Set to last day of previous month
        }
        break;
      case 'quarterly':
        date.setMonth(date.getMonth() + 3);
        if (date.getDate() !== originalDay) {
          date.setDate(0);
        }
        break;
      case 'semi-annually':
        date.setMonth(date.getMonth() + 6);
        if (date.getDate() !== originalDay) {
          date.setDate(0);
        }
        break;
      case 'weekly':
        date.setDate(date.getDate() + 7);
        break;
      case 'daily':
        date.setDate(date.getDate() + 1);
        break;
      default:
        console.warn('Unknown frequency:', frequency);
    }

    return date;
  };

  const handleAddToJournal = (tx) => {
    const today = new Date().toISOString().split('T')[0];

    let postings = [];

    if (tx.postings && Array.isArray(tx.postings) && tx.postings.length > 0) {
      postings = tx.postings.map(p => {
        if (Array.isArray(p)) {
          return {
            account: p[0] || '',
            amount: p[1] !== undefined && p[1] !== null && p[1] !== '' ? p[1].toString() : ''
          };
        } else {
          return {
            account: p.account || '',
            amount: p.amount !== undefined && p.amount !== null ? p.amount.toString() : ''
          };
        }
      });
    } else {
      if (tx.debit_account) {
        postings.push({
          account: tx.debit_account,
          amount: tx.ask_amount ? '' : (tx.amount ? tx.amount.toString() : '')
        });
      }
      if (tx.credit_account) {
        postings.push({
          account: tx.credit_account,
          amount: tx.ask_amount ? '' : (tx.amount ? (-tx.amount).toString() : '')
        });
      }
    }

    if (postings.length < 2) {
      postings = [
        { account: '', amount: '' },
        { account: '', amount: '' }
      ];
    }

    setTransactionToAdd({
      date: tx.due_date || today,
      payee: tx.payee,
      note: '',
      postings: postings
    });
    setEditingTransaction(tx);
    setShowEditModal(true);
  };

  const handleConfirmAddToJournal = async () => {
    try {
      const { date, payee, note, postings } = transactionToAdd;

      if (!date || !payee) {
        alert('Please fill in date and payee');
        return;
      }

      if (!postings || postings.length < 2) {
        alert('Transaction must have at least 2 postings');
        return;
      }

      const invalidPostings = postings.filter(p => !p.account || !p.account.trim());
      if (invalidPostings.length > 0) {
        alert('All postings must have an account');
        return;
      }

      const postingsWithAmount = postings.filter(p => p.amount && p.amount.trim());
      if (postingsWithAmount.length < postings.length - 1) {
        alert('At least all but one posting must have an amount');
        return;
      }

      const noteText = note ? ` | ${note}` : '';
      let entry = `${date} ${payee}${noteText}\n`;

      postings.forEach(posting => {
        const amountText = posting.amount && posting.amount.trim() ? posting.amount : '';
        entry += `    ${posting.account.padEnd(50)} ${amountText}\n`;
      });

      await axios.post(getApiUrl('/api/journal/append'), {
        journalPath: config.journalPath,
        transaction: entry
      });

      // Update due date in transactions.json
      const updatedTx = {
        ...editingTransaction,
        dueDate: calculateNextDueDate(editingTransaction.dueDate, editingTransaction.frequency),
        due_date: calculateNextDueDate(editingTransaction.dueDate, editingTransaction.frequency).toISOString().split('T')[0]
      };

      const updatedTransactions = upcomingTransactions.map(t =>
        t.payee === editingTransaction.payee && t.due_date === editingTransaction.due_date ? updatedTx : t
      );

      await saveTransactions(updatedTransactions.map(t => ({
        ...t,
        due_date: typeof t.due_date === 'string' ? t.due_date : t.dueDate.toISOString().split('T')[0]
      })));

      setShowEditModal(false);
      setEditingTransaction(null);
      alert('Transaction added to journal!');

      // Reload dashboard data
      loadDashboardData();
    } catch (err) {
      alert('Error adding transaction: ' + err.message);
    }
  };

  const parseDescription = (description) => {
    if (!description || typeof description !== 'string') return { payee: description, note: null };

    // hledger format: "Payee | Note"
    const parts = description.split(' | ');
    if (parts.length === 1) {
      return { payee: parts[0], note: null };
    }
    return { payee: parts[0], note: parts.slice(1).join(' | ') };
  };

  const formatAccountName = (fullAccount) => {
    if (!fullAccount || typeof fullAccount !== 'string') return fullAccount;
    const parts = fullAccount.split(':');
    if (parts.length <= 1) return fullAccount;
    return parts.slice(1).map((part, idx) => (
      <React.Fragment key={idx}>
        {idx > 0 && <span style={{ padding: '0 8px', color: '#6e7681' }}>›</span>}
        {part}
      </React.Fragment>
    ));
  };

  const getAccountType = (accountName) => {
    if (!accountName || typeof accountName !== 'string') return 'other';
    if (accountName.startsWith('Assets')) return 'asset';
    if (accountName.startsWith('Liabilities')) return 'liability';
    if (accountName.startsWith('Income')) return 'income';
    if (accountName.startsWith('Expenses')) return 'expense';
    if (accountName.startsWith('Equity')) return 'equity';
    return 'other';
  };

  const toggleRecentExpanded = (txIndex) => {
    const newExpanded = new Set(expandedRecentTxs);
    if (newExpanded.has(txIndex)) {
      newExpanded.delete(txIndex);
    } else {
      newExpanded.add(txIndex);
    }
    setExpandedRecentTxs(newExpanded);
  };

  const handleTagClick = (tag, e) => {
    e.stopPropagation(); // Prevent expanding/collapsing the transaction
    const tagStr = typeof tag === 'string' ? tag : String(tag);
    const searchTag = tagStr.replace(',', ':');
    console.log('Dashboard tag clicked:', searchTag);
    navigate(`/transactions?search=${encodeURIComponent(searchTag)}`);
  };

  const handleStatusClick = async (tx, e) => {
    e.stopPropagation(); // Prevent expanding/collapsing the transaction

    // Cycle through statuses: unmarked → Pending → Cleared → unmarked
    let newStatus = '';
    if (!tx.tstatus || tx.tstatus === 'Unmarked') {
      newStatus = 'Pending';
    } else if (tx.tstatus === 'Pending') {
      newStatus = 'Cleared';
    } else if (tx.tstatus === 'Cleared') {
      newStatus = ''; // unmarked
    }

    try {
      await axios.post(getApiUrl('/api/journal/update-status'), {
        journalPath: config.journalPath,
        date: tx.tdate,
        description: tx.tdescription,
        newStatus: newStatus
      });

      // Update status locally without reloading
      setRecentTransactions(prevTransactions =>
        prevTransactions.map(t =>
          t.tdate === tx.tdate && t.tdescription === tx.tdescription
            ? { ...t, tstatus: newStatus || 'Unmarked' }
            : t
        )
      );
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update transaction status: ' + (err.response?.data?.error || err.message));
    }
  };

  const getAllTags = (tx) => {
    const tags = new Set();
    if (tx.ttags) {
      tx.ttags.forEach(tag => {
        let tagName;
        if (Array.isArray(tag)) {
          tagName = tag[0];
          if (tag[1]) tagName += ':' + tag[1];
        } else {
          tagName = typeof tag === 'string' ? tag : String(tag);
        }
        if (tagName) tags.add(tagName);
      });
    }
    if (tx.tpostings) {
      tx.tpostings.forEach(posting => {
        if (posting.ptags) {
          posting.ptags.forEach(tag => {
            let tagName;
            if (Array.isArray(tag)) {
              tagName = tag[0];
              if (tag[1]) tagName += ':' + tag[1];
            } else {
              tagName = typeof tag === 'string' ? tag : String(tag);
            }
            if (tagName) tags.add(tagName);
          });
        }
      });
    }
    return Array.from(tags);
  };

  const getTxNetAmount = (tx) => {
    let maxAmount = 0;
    if (tx.tpostings) {
      tx.tpostings.forEach(posting => {
        if (posting.pamount && posting.pamount.length > 0) {
          const amt = Math.abs(posting.pamount[0]?.aquantity?.floatingPoint || 0);
          if (amt > maxAmount) maxAmount = amt;
        }
      });
    }
    return maxAmount;
  };

  const getAccountTypeForFilter = (account) => {
    if (!account || typeof account !== 'string') return 'other';
    if (account.startsWith('Assets')) return 'assets';
    if (account.startsWith('Liabilities')) return 'liabilities';
    if (account.startsWith('Income')) return 'income';
    if (account.startsWith('Expenses')) return 'expenses';
    if (account.startsWith('Equity')) return 'equity';
    return 'other';
  };

  const toggleAccountType = (type) => {
    setAccountTypeFilter(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const filterBalancesByType = (balances) => {
    // In --tree mode, we need to track which top-level account we're under
    // because children don't have full paths
    let currentTopLevel = null;
    let currentDepth = 0;
    const topLevelStack = []; // Track parent hierarchy

    const filtered = balances.filter(entry => {
      const shortName = entry[0];
      const fullName = entry[1];
      const depth = entry[2];

      if (!fullName || typeof fullName !== 'string') {
        return false;
      }

      // Update our tracking of which top-level account we're under
      if (depth === 0) {
        // This is a top-level account
        currentTopLevel = fullName;
        topLevelStack.length = 0;
        topLevelStack.push(fullName);
      } else {
        // Adjust stack based on depth changes
        while (topLevelStack.length > depth) {
          topLevelStack.pop();
        }
        if (topLevelStack.length === depth) {
          topLevelStack.push(fullName);
        }
      }

      // Determine account type based on the top-level parent
      const accountType = topLevelStack[0] || fullName;

      if (accountType === 'Assets' || accountType.startsWith('Assets:')) {
        return accountTypeFilter.assets;
      }
      if (accountType === 'Liabilities' || accountType.startsWith('Liabilities:')) {
        return accountTypeFilter.liabilities;
      }
      if (accountType === 'Income' || accountType.startsWith('Income:')) {
        return accountTypeFilter.income;
      }
      if (accountType === 'Expenses' || accountType.startsWith('Expenses:')) {
        return accountTypeFilter.expenses;
      }
      if (accountType === 'Equity' || accountType.startsWith('Equity:')) {
        return accountTypeFilter.equity;
      }

      // Show other accounts by default
      return true;
    });

    return filtered;
  };

  const calculateNetWorth = () => {
    let assets = 0;
    let liabilities = 0;

    balances.forEach(entry => {
      // Entry format: [accountName, fullName, depth, [amounts]]
      const accountName = entry[1];
      const amounts = entry[3];

      if (amounts && amounts.length > 0 && typeof accountName === 'string') {
        const amount = amounts[0]?.aquantity?.floatingPoint || 0;

        if (accountName.startsWith('Assets')) {
          assets += amount;
        } else if (accountName.startsWith('Liabilities')) {
          liabilities += amount;
        }
      }
    });

    return assets - Math.abs(liabilities);
  };

  const renderBalanceTree = (entries) => {
    return entries.map((entry, index) => {
      // Entry format: [accountName, fullName, depth, [amounts]]
      const shortName = entry[0];
      const fullName = entry[1];
      const depth = entry[2];
      const amounts = entry[3];

      // Skip if shortName is not a valid string
      if (!shortName || typeof shortName !== 'string') return null;

      // Always extract just the leaf name (last part after colon)
      const parts = fullName.split(':');
      const leafName = parts[parts.length - 1];

      const amount = amounts && amounts.length > 0
        ? amounts[0]?.aquantity?.floatingPoint || 0
        : 0;

      const commodity = amounts && amounts.length > 0
        ? amounts[0]?.acommodity || ''
        : '';

      const type = getAccountTypeForFilter(fullName);
      const isNegative = amount < 0;

      // Determine if this is a grouping header (depth 1 subcategory like "Checking", "Savings")
      const isGroupHeader = depth === 1 && parts.length === 2; // e.g., "Assets:Checking"
      const isLeafAccount = depth >= 2; // e.g., "Assets:Checking:Main Checking"

      // For group headers, show bold with subtotal
      if (isGroupHeader) {
        return (
          <div key={index} className={`balance-row balance-group-header balance-type-${type.replace(/s$/, '')}`}>
            <span className="account-name" style={{ fontWeight: 600, fontSize: '14px' }}>
              {leafName}
            </span>
            <span className={`account-balance ${isNegative ? 'negative' : 'positive'}`} style={{ fontWeight: 600 }}>
              {formatCurrency(amount, commodity || '$')}
            </span>
          </div>
        );
      }

      // For leaf accounts, show with link and indent
      if (isLeafAccount) {
        return (
          <div key={index} className={`balance-row balance-level-${depth} balance-type-${type.replace(/s$/, '')}`}>
            <Link
              to={`/transactions?account=${encodeURIComponent(fullName)}`}
              className="account-name"
              style={{
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer',
                paddingLeft: '1.5rem'
              }}
              title={`View transactions for ${fullName}`}
            >
              {leafName}
            </Link>
            <span className={`account-balance ${isNegative ? 'negative' : 'positive'}`}>
              {formatCurrency(amount, commodity || '$')}
            </span>
          </div>
        );
      }

      // Default rendering for other depths
      return (
        <div key={index} className={`balance-row balance-level-${depth} balance-type-${type.replace(/s$/, '')}`}>
          <span className="account-name" style={{ paddingLeft: `${depth * 1.5}rem` }}>
            {leafName}
          </span>
          <span className={`account-balance ${isNegative ? 'negative' : 'positive'}`}>
            {formatCurrency(amount, commodity || '$')}
          </span>
        </div>
      );
    });
  };

  const getDaysUntilDue = (dueDate) => {
    const today = new Date();
    const days = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    return days;
  };

  const getUrgencyClass = (days) => {
    if (days < 0) return 'overdue';
    if (days <= 3) return 'urgent';
    if (days <= 7) return 'soon';
    return 'normal';
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="page-header">
          <h1>Dashboard</h1>
        </div>
        <div className="alert alert-danger">{error}</div>
        <button className="btn btn-primary" onClick={loadDashboardData}>
          Retry
        </button>
      </div>
    );
  }

  const netWorth = calculateNetWorth();

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Overview of your financial accounts</p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-3" style={{ marginBottom: '2rem' }}>
        <div className="stat-card">
          <div className="stat-label">Net Worth</div>
          <div className={`stat-value ${netWorth >= 0 ? 'text-success' : 'text-danger'}`}>
            {formatCurrency(netWorth)}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Upcoming Transactions</div>
          <div className="stat-value">
            {upcomingTransactions.length}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Last Updated</div>
          <div className="stat-value" style={{ fontSize: '16px' }}>
            {new Date().toLocaleDateString()}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '25% 1fr 25%', gap: '1.5rem' }}>
        {/* Account Balances */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Account Balances</h2>
            <button className="btn btn-sm" onClick={loadDashboardData}>
              Refresh
            </button>
          </div>
          <div className="card-body">
            {/* Account Type Filter */}
            <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #30363d' }}>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '13px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={accountTypeFilter.assets}
                    onChange={() => toggleAccountType('assets')}
                  />
                  <span>Assets</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={accountTypeFilter.liabilities}
                    onChange={() => toggleAccountType('liabilities')}
                  />
                  <span>Liabilities</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={accountTypeFilter.income}
                    onChange={() => toggleAccountType('income')}
                  />
                  <span>Income</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={accountTypeFilter.expenses}
                    onChange={() => toggleAccountType('expenses')}
                  />
                  <span>Expenses</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={accountTypeFilter.equity}
                    onChange={() => toggleAccountType('equity')}
                  />
                  <span>Equity</span>
                </label>
              </div>
            </div>

            <div className="balance-tree">
              {balances.length > 0 ? renderBalanceTree(filterBalancesByType(balances)) : (
                <div className="empty-state">
                  <p>No accounts found</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Budget Summary */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Budget Summary</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {/* Month Navigation */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <button
                  className="btn btn-sm"
                  onClick={() => navigateBudgetMonth(-1)}
                  title="Previous month"
                  style={{ padding: '0.25rem 0.5rem', fontSize: '14px' }}
                >
                  ◀
                </button>
                <span style={{ fontSize: '13px', minWidth: '80px', textAlign: 'center', color: '#8b949e' }}>
                  {new Date(budgetPeriod + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </span>
                <button
                  className="btn btn-sm"
                  onClick={() => navigateBudgetMonth(1)}
                  title="Next month"
                  style={{ padding: '0.25rem 0.5rem', fontSize: '14px' }}
                >
                  ▶
                </button>
                <button
                  className="btn btn-sm"
                  onClick={goToCurrentMonth}
                  title="Current month"
                  style={{ padding: '0.25rem 0.5rem', fontSize: '12px' }}
                >
                  Today
                </button>
              </div>
              <a href="/budget" className="btn btn-sm">
                View All
              </a>
            </div>
          </div>
          <div className="card-body">
            {budgets.length === 0 ? (
              <div className="empty-state">
                <p>No budgets configured</p>
                <p style={{ fontSize: '13px', color: '#8b949e', marginTop: '0.5rem' }}>
                  Add periodic transaction rules to your journal file to track budgets
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {budgets.map((budget, index) => {
                  const accountName = budget.accountName || '';
                  const leafName = accountName.split(':').pop() || accountName;
                  const actual = budget.actual || 0;
                  const budgeted = budget.budgeted || 0;

                  const remaining = budgeted - actual;
                  const percentage = budgeted > 0 ? Math.min((actual / budgeted) * 100, 100) : 0;

                  return (
                    <Link
                      key={index}
                      to={`/transactions?account=${encodeURIComponent(accountName)}&month=${budgetPeriod}`}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                        textDecoration: 'none',
                        color: 'inherit',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        transition: 'background-color 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      title={`View transactions for ${accountName}`}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '13px', fontWeight: 500 }}>{leafName}</span>
                        <span style={{ fontSize: '12px', color: '#8b949e' }}>
                          {formatCurrency(actual)} / {formatCurrency(budgeted)}
                        </span>
                      </div>
                      <div style={{ position: 'relative', width: '100%', height: '8px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          height: '100%',
                          width: `${percentage}%`,
                          backgroundColor: percentage >= 100 ? 'var(--danger)' : percentage >= 80 ? 'var(--warning)' : 'var(--success)',
                          transition: 'width 0.3s ease',
                          borderRadius: '4px'
                        }} />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#8b949e' }}>
                        <span>Spent: {formatCurrency(actual)}</span>
                        <span>Remaining: {formatCurrency(remaining)}</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Transactions */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Upcoming Transactions</h2>
            {config.transactionsPath && (
              <a href="/upcoming" className="btn btn-sm">
                View All
              </a>
            )}
          </div>
          <div className="card-body">
            {!config.transactionsPath ? (
              <div className="empty-state">
                <p>No upcoming transactions configured</p>
                <a href="/settings" className="btn btn-primary btn-sm">
                  Set Up in Settings
                </a>
              </div>
            ) : upcomingTransactions.length === 0 ? (
              <div className="empty-state">
                <p>No upcoming transactions in the next 14 days</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {upcomingTransactions.map((tx, index) => {
                  const daysUntil = getDaysUntilDue(tx.dueDate);
                  const urgency = getUrgencyClass(daysUntil);

                  return (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: 'var(--bg-secondary)',
                      borderRadius: '4px',
                      borderLeft: `3px solid ${urgency === 'overdue' ? 'var(--danger)' : urgency === 'urgent' ? 'var(--warning)' : urgency === 'soon' ? '#58a6ff' : 'var(--border-color)'}`,
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onClick={() => handleAddToJournal(tx)}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                    title="Click to add to journal"
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: 500,
                          fontSize: '13px',
                          marginBottom: '2px'
                        }}>
                          {tx.payee}
                        </div>
                        <div style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '11px',
                          color: '#8b949e'
                        }}>
                          {tx.debit_account.split(':').pop()}
                        </div>
                      </div>
                      <div style={{ flexShrink: 0, textAlign: 'right' }}>
                        <div style={{
                          whiteSpace: 'nowrap',
                          fontWeight: 500,
                          fontSize: '13px',
                          marginBottom: '2px'
                        }}>
                          {tx.ask_amount ? 'TBD' : formatCurrency(tx.amount)}
                        </div>
                        <div style={{
                          fontSize: '10px',
                          color: urgency === 'overdue' ? 'var(--danger)' : urgency === 'urgent' ? 'var(--warning)' : '#8b949e',
                          whiteSpace: 'nowrap'
                        }}>
                          {daysUntil < 0 ? `${Math.abs(daysUntil)}d overdue` : daysUntil === 0 ? 'today' : `in ${daysUntil}d`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Recent Transactions</h2>
          <a href="/transactions" className="btn btn-sm">
            View All
          </a>
        </div>
        <div className="card-body">
          {recentTransactions.length === 0 ? (
            <div className="empty-state">
              <p>No recent transactions found</p>
            </div>
          ) : (
            <div className="transactions-list">
              {recentTransactions.map((tx, index) => {
                const isExpanded = expandedRecentTxs.has(index);
                const netAmount = getTxNetAmount(tx);
                const statusIcon = tx.tstatus === 'Cleared' ? '✓' : tx.tstatus === 'Pending' ? '!' : '';
                const allTags = getAllTags(tx);
                // Get order number from hledger's tcode field (transaction code)
                const orderNumber = tx.tcode || null;
                const { payee, note } = parseDescription(tx.tdescription);

                return (
                  <div key={index} className={`transaction-card ${isExpanded ? 'expanded' : ''}`}>
                    {/* Transaction Header */}
                    <div
                      className="transaction-header"
                      onClick={() => toggleRecentExpanded(index)}
                      style={{ cursor: 'pointer', padding: '8px 12px', borderBottom: '1px solid #30363d', display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px' }}
                    >
                      <span className="text-mono" style={{ color: '#8b949e', minWidth: '80px' }}>{tx.tdate}</span>
                      <span style={{ flex: 1 }}>
                        <span style={{ color: '#e6edf3', fontWeight: 500 }}>{payee}</span>
                        {note && <span style={{ color: '#6e7681', marginLeft: '12px' }}>{note}</span>}
                      </span>
                      {allTags.length > 0 && (
                        <span style={{ fontSize: '11px', color: '#79c0ff', display: 'flex', gap: '6px' }}>
                          {allTags.map((tag, tagIdx) => {
                            const tagStr = typeof tag === 'string' ? tag : String(tag);
                            return (
                              <span
                                key={tagIdx}
                                onClick={(e) => handleTagClick(tag, e)}
                                style={{
                                  cursor: 'pointer',
                                  padding: '2px 4px',
                                  borderRadius: '3px',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(121, 192, 255, 0.2)'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                title={`Filter by ${tagStr.replace(',', ':')}`}
                              >
                                #{tagStr.replace(',', ':')}
                              </span>
                            );
                          })}
                        </span>
                      )}
                      <span
                        onClick={(e) => handleStatusClick(tx, e)}
                        style={{
                          minWidth: '20px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          color: tx.tstatus === 'Cleared' ? '#3fb950' : tx.tstatus === 'Pending' ? '#d29922' : '#6e7681',
                          fontSize: '14px',
                          padding: '2px 4px',
                          borderRadius: '3px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        title={`Status: ${tx.tstatus || 'Unmarked'} (click to change)`}
                      >
                        {statusIcon || '○'}
                      </span>
                      <span className="text-mono" style={{ minWidth: '100px', textAlign: 'right' }}>{formatCurrency(netAmount)}</span>
                      <span style={{ color: '#8b949e' }}>{isExpanded ? '▲' : '▼'}</span>
                    </div>

                    {/* Transaction Details (when expanded) */}
                    {isExpanded && (
                      <div className="transaction-details" style={{ padding: '8px 12px', backgroundColor: '#0d1117', fontSize: '12px' }}>
                        {/* Order Number */}
                        {orderNumber && (
                          <div style={{ marginBottom: '8px', fontSize: '11px', color: '#8b949e' }}>
                            <span style={{ color: '#6e7681' }}>Order: </span>
                            <span className="text-mono" style={{ color: '#a371f7' }}>{orderNumber}</span>
                          </div>
                        )}

                        {/* Postings */}
                        {tx.tpostings && tx.tpostings.map((posting, pIdx) => {
                          const accountType = getAccountType(posting.paccount);
                          const amount = posting.pamount && posting.pamount.length > 0
                            ? posting.pamount[0]?.aquantity?.floatingPoint || 0
                            : 0;
                          const commodity = posting.pamount && posting.pamount.length > 0
                            ? posting.pamount[0]?.acommodity || ''
                            : '';

                          // Clean comment: remove tag syntax if tags are parsed separately
                          let cleanComment = posting.pcomment || '';
                          if (posting.ptags && posting.ptags.length > 0) {
                            posting.ptags.forEach(tag => {
                              // Tags can be arrays like ["FinnBirthday", "2024"] or strings
                              let fullTag;

                              if (Array.isArray(tag)) {
                                // Build full tag: "FinnBirthday:2024" or just "FinnBirthday" if no value
                                if (tag[1]) {
                                  fullTag = `${tag[0]}:${tag[1]}`;
                                } else {
                                  fullTag = tag[0];
                                }
                              } else {
                                fullTag = typeof tag === 'string' ? tag : String(tag);
                                fullTag = fullTag.replace(',', ':');
                              }

                              if (!fullTag) return;

                              // Remove the complete tag pattern (with or without #)
                              cleanComment = cleanComment.replace(new RegExp(`${fullTag}`, 'g'), '');
                              cleanComment = cleanComment.replace(new RegExp(`#${fullTag}`, 'g'), '');
                            });
                            cleanComment = cleanComment.replace(/^[;,:\s]+|[;,:\s]+$/g, '').trim();
                          }

                          return (
                            <div key={pIdx} style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <span className={`badge badge-${accountType}`} style={{ fontSize: '10px', padding: '2px 6px', minWidth: '60px', textAlign: 'center' }}>
                                {accountType}
                              </span>
                              <span style={{ flex: 1, color: '#c9d1d9' }}>
                                {formatAccountName(posting.paccount)}
                              </span>
                              {cleanComment && (
                                <span style={{ color: '#8b949e', fontSize: '11px' }}>; {cleanComment}</span>
                              )}
                              {posting.ptags && posting.ptags.length > 0 && (
                                <span style={{ fontSize: '10px', color: '#79c0ff', display: 'flex', gap: '4px' }}>
                                  {posting.ptags.map((tag, tagIdx) => {
                                    const tagStr = typeof tag === 'string' ? tag : String(tag);
                                    return (
                                      <span
                                        key={tagIdx}
                                        onClick={(e) => handleTagClick(tag, e)}
                                        style={{
                                          cursor: 'pointer',
                                          padding: '2px 4px',
                                          borderRadius: '3px',
                                          transition: 'background-color 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(121, 192, 255, 0.2)'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        title={`Filter by ${tagStr.replace(',', ':')}`}
                                      >
                                        #{tagStr.replace(',', ':')}
                                      </span>
                                    );
                                  })}
                                </span>
                              )}
                              {amount !== 0 && (
                                <span className="text-mono" style={{ minWidth: '100px', textAlign: 'right', color: amount > 0 ? '#3fb950' : '#f85149' }}>
                                  {formatCurrency(amount, commodity || '$')}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Add to Journal Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Review Transaction</h2>
              <button
                className="modal-close"
                onClick={() => setShowEditModal(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem', color: '#8b949e', fontSize: '13px' }}>
                Review and edit the transaction details before adding to journal
              </p>
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input
                  type="date"
                  className="form-input"
                  value={transactionToAdd.date}
                  onChange={(e) => setTransactionToAdd({ ...transactionToAdd, date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Payee *</label>
                <input
                  type="text"
                  className="form-input"
                  value={transactionToAdd.payee}
                  onChange={(e) => setTransactionToAdd({ ...transactionToAdd, payee: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Note (Optional)</label>
                <input
                  type="text"
                  className="form-input"
                  value={transactionToAdd.note}
                  onChange={(e) => setTransactionToAdd({ ...transactionToAdd, note: e.target.value })}
                  placeholder="Additional note or description"
                />
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label className="form-label" style={{ margin: 0 }}>Postings *</label>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => {
                      setTransactionToAdd({
                        ...transactionToAdd,
                        postings: [...transactionToAdd.postings, { account: '', amount: '' }]
                      });
                    }}
                  >
                    + Add Posting
                  </button>
                </div>
                <p style={{ fontSize: '12px', color: '#8b949e', marginBottom: '0.75rem' }}>
                  Add multiple postings for split transactions (e.g., mortgage with principal, interest, escrow). Leave one amount blank to auto-calculate.
                </p>

                {transactionToAdd.postings.map((posting, index) => (
                  <div key={index} style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 150px 40px',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                    padding: '0.75rem',
                    background: 'var(--bg-primary)',
                    borderRadius: '4px',
                    border: '1px solid var(--border)'
                  }}>
                    <div>
                      <label style={{ fontSize: '11px', color: '#8b949e', display: 'block', marginBottom: '0.25rem' }}>
                        Account
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        list="accounts-list-modal"
                        value={posting.account}
                        onChange={(e) => {
                          const newPostings = [...transactionToAdd.postings];
                          newPostings[index].account = e.target.value;
                          setTransactionToAdd({ ...transactionToAdd, postings: newPostings });
                        }}
                        placeholder="e.g., Expenses:Car:Interest"
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: '#8b949e', display: 'block', marginBottom: '0.25rem' }}>
                        Amount
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={posting.amount}
                        onChange={(e) => {
                          const newPostings = [...transactionToAdd.postings];
                          newPostings[index].amount = e.target.value;
                          setTransactionToAdd({ ...transactionToAdd, postings: newPostings });
                        }}
                        placeholder="100 or -100"
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => {
                          const newPostings = transactionToAdd.postings.filter((_, i) => i !== index);
                          setTransactionToAdd({ ...transactionToAdd, postings: newPostings });
                        }}
                        disabled={transactionToAdd.postings.length <= 2}
                        title="Remove posting"
                        style={{ padding: '0.25rem 0.5rem' }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <datalist id="accounts-list-modal">
                {accounts.map((account, index) => (
                  <option key={index} value={account} />
                ))}
              </datalist>

              {/* Journal Preview */}
              <div className="form-group" style={{ marginTop: '1.5rem' }}>
                <label className="form-label">Preview - Journal Entry</label>
                <p style={{ fontSize: '12px', color: '#8b949e', marginBottom: '0.5rem' }}>
                  This is what will be written to your journal file:
                </p>
                <pre style={{
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  padding: '1rem',
                  fontSize: '12px',
                  fontFamily: 'var(--font-mono)',
                  color: '#e6edf3',
                  overflow: 'auto',
                  whiteSpace: 'pre'
                }}>
                  {(() => {
                    const { date, payee, note, postings } = transactionToAdd;
                    if (!date || !payee) return '(fill in date and payee to see preview)';

                    const noteText = note ? ` | ${note}` : '';
                    let preview = `${date} ${payee}${noteText}\n`;

                    postings.forEach(posting => {
                      const amountText = posting.amount && posting.amount.trim() ? posting.amount : '';
                      preview += `    ${posting.account.padEnd(50)} ${amountText}\n`;
                    });

                    return preview;
                  })()}
                </pre>
              </div>

              <div className="modal-actions">
                <button className="btn" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleConfirmAddToJournal}>
                  Add to Journal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
